import { MessageImages } from "@/components/MessageImages";
import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, Clock, CheckCircle, XCircle, User, ChevronRight, BellOff, Mail, Phone, UserX } from "lucide-react";

interface SupportRequest {
  attachmentIds?: string[];
  id: number;
  /** Null for guest (logged-out) requests — see guestName/guestEmail/guestPhone. */
  userId: number | null;
  status: "pending" | "accepted" | "closed";
  category: string | null;
  initialMessage: string;
  guestName: string | null;
  guestEmail: string | null;
  guestPhone: string | null;
  conversationId: number | null;
  acceptedById: number | null;
  createdAt: string;
  userName: string | null;
  userEmail: string | null;
  userAvatarUrl: string | null;
}

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const STATUS_CONFIG = {
  pending: { label: "Pending", icon: Clock, color: "text-amber-600 bg-amber-50 dark:bg-amber-900/20 border-amber-200" },
  accepted: { label: "Accepted", icon: CheckCircle, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  closed: { label: "Closed", icon: XCircle, color: "text-slate-500 bg-slate-50 border-slate-200" },
};

/** Mirrors SUPPORT_CATEGORIES in api-server/src/routes/support.ts. */
const CATEGORY_LABELS: Record<string, string> = {
  unsubscribe: "Unsubscribe from marketing",
  order: "Order problem",
  delivery: "Delivery / driver",
  payment: "Payment / refund",
  account: "Account / login",
  business: "Business / listing",
  report: "Report content",
  other: "Something else",
};

const CATEGORY_FILTERS = ["all", ...Object.keys(CATEGORY_LABELS)] as const;

function SupportCard({ request, onAction }: { request: SupportRequest; onAction: () => void }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [busy, setBusy] = useState(false);
  const cfg = STATUS_CONFIG[request.status];
  const StatusIcon = cfg.icon;

  // A guest has no account, so there is nothing to open a conversation into.
  // Reply on the email/phone they supplied, then close.
  const isGuest = request.userId == null;
  const displayName = isGuest
    ? (request.guestName ?? "Guest")
    : (request.userName ?? `User #${request.userId}`);
  const contactEmail = isGuest ? request.guestEmail : request.userEmail;
  const contactPhone = isGuest ? request.guestPhone : null;
  const isOptOut = request.category === "unsubscribe";

  const accept = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/support-requests/${request.id}/accept`, {
        method: "PATCH",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error();
      const data = await res.json() as { conversationId: number };
      toast({ title: "Request accepted — conversation opened" });
      onAction();
      navigate(`/messages?conversation=${data.conversationId}`);
    } catch {
      toast({ title: "Failed to accept request", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/support-requests/${request.id}/close`, {
        method: "PATCH",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error();
      toast({ title: "Request closed" });
      onAction();
    } catch {
      toast({ title: "Failed to close request", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`bg-white border rounded-xl p-4 ${isOptOut && request.status === "pending" ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200"}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isGuest ? "bg-slate-100" : "bg-primary/10"}`}>
          {isGuest ? <UserX size={16} className="text-slate-500" /> : <User size={16} className="text-primary" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-semibold text-sm text-slate-800">{displayName}</p>
                {isGuest && (
                  <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                    No account
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {new Date(request.createdAt).toLocaleString("en-GB", {
                  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {request.category && (
                <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${
                  isOptOut
                    ? "text-amber-700 bg-amber-50 border-amber-300"
                    : "text-slate-600 bg-slate-50 border-slate-200"
                }`}>
                  {isOptOut && <BellOff size={11} />}
                  {CATEGORY_LABELS[request.category] ?? request.category}
                </span>
              )}
              <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                <StatusIcon size={11} />
                {cfg.label}
              </span>
            </div>
          </div>

          {/* Contact details. For a guest this is the ONLY way to reach them,
              and for an opt-out it is the identifier that has to be actioned —
              so both are click-to-use rather than something to retype. */}
          {(contactEmail || contactPhone) && (
            <div className="flex items-center gap-3 flex-wrap mt-2">
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline break-all">
                  <Mail size={11} className="flex-shrink-0" /> {contactEmail}
                </a>
              )}
              {contactPhone && (
                <a href={`tel:${contactPhone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                  <Phone size={11} className="flex-shrink-0" /> {contactPhone}
                </a>
              )}
            </div>
          )}

          <div className="mt-2 bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100">
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{request.initialMessage}</p>
            <MessageImages ids={request.attachmentIds}/>
          </div>

          {isOptOut && (
            <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <strong className="font-bold">Marketing opt-out.</strong> Suppress the address or number above,
              then close. Legally this must be actioned promptly and free of charge.
            </p>
          )}

          {request.status === "pending" && (
            <div className="flex gap-2 mt-3">
              {/* No account, no inbox — the accept flow would have nowhere to
                  put the conversation, so it isn't offered. The API rejects it
                  too; this just stops staff clicking a button that can't work. */}
              {!isGuest && (
                <button
                  onClick={() => void accept()}
                  disabled={busy}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  <MessageSquare size={12} />
                  {busy ? "Accepting…" : "Accept & Open Chat"}
                  <ChevronRight size={12} />
                </button>
              )}
              {isGuest && (contactEmail || contactPhone) && (
                <a
                  href={contactEmail
                    ? `mailto:${contactEmail}?subject=${encodeURIComponent("Re: your Ayrshire Hub support request")}`
                    : `tel:${(contactPhone ?? "").replace(/\s/g, "")}`}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white px-4 py-1.5 rounded-lg transition-colors"
                >
                  {contactEmail ? <Mail size={12} /> : <Phone size={12} />}
                  {contactEmail ? "Reply by email" : "Call back"}
                </a>
              )}
              <button
                onClick={() => void close()}
                disabled={busy}
                className="flex items-center gap-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
              >
                <XCircle size={12} />
                {isGuest ? "Mark done" : "Close"}
              </button>
            </div>
          )}

          {request.status === "accepted" && request.conversationId && (
            <div className="flex gap-2 mt-3">
              <a
                href={`/messages?conversationId=${request.conversationId}`}
                className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-lg transition-colors"
              >
                <MessageSquare size={12} />
                Open Conversation
              </a>
              <button
                onClick={() => void close()}
                disabled={busy}
                className="flex items-center gap-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
              >
                <XCircle size={12} />
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminSupport() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"pending" | "accepted" | "closed" | "all">("pending");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const isStaff = user?.role === "admin" || (user as { isModerator?: boolean } | null)?.isModerator;
  if (!isStaff) return <Redirect to="/" />;

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `/api/admin/support-requests?status=${statusFilter}&category=${categoryFilter}`,
        { headers: authHeaders() },
      );
      const data = await res.json() as { items: SupportRequest[] };
      setRequests(data.items ?? []);
    } catch {
      toast({ title: "Failed to load support requests", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, toast]);

  useEffect(() => { void fetchRequests(); }, [fetchRequests]);

  const pendingCount = requests.filter(r => r.status === "pending").length;

  return (
    <AdminLayout current="/admin/support">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <MessageSquare size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Support Requests</h1>
            <p className="text-sm text-slate-500">
              {statusFilter === "pending" && pendingCount > 0
                ? `${pendingCount} pending request${pendingCount === 1 ? "" : "s"} waiting`
                : "User support queue"}
            </p>
          </div>
        </div>

        <div className="flex gap-2 mb-3 flex-wrap">
          {(["pending", "accepted", "closed", "all"] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                statusFilter === s
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Category filter. The point of this row is "Unsubscribe from
            marketing": those requests carry a legal deadline, so they have to
            be isolatable in one click rather than read out of free text. */}
        <div className="flex gap-2 mb-5 flex-wrap items-center">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-0.5">Topic</span>
          {CATEGORY_FILTERS.map(c => (
            <button
              key={c}
              onClick={() => setCategoryFilter(c)}
              className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                categoryFilter === c
                  ? c === "unsubscribe"
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-slate-800 text-white border-slate-800"
                  : c === "unsubscribe"
                    ? "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {c === "unsubscribe" && <BellOff size={11} />}
              {c === "all" ? "All topics" : (CATEGORY_LABELS[c] ?? c)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <MessageSquare size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">No {statusFilter === "all" ? "" : statusFilter} support requests</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map(r => (
              <SupportCard key={r.id} request={r} onAction={() => void fetchRequests()} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
